import {
    Component,
    effect,
    inject,
    input,
    output,
    signal,
} from '@angular/core';

import { FormsModule } from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';

import {
    LayoutService
} from '@/app/core/auth-layout/service/layout.service';

import {
    UserCompany
} from '@/app/features/users/models/user.model';

import {
    ChangePasswordDialogComponent
} from '../change-password/change-password-dialog.component';
import { CompanyService } from '@/app/shared/services/company.service';
import { Market, MarketService } from '@/app/shared/services/market.service';
import { UserPreferences } from '@/app/features/authentication/services/auth.service';

@Component({
    selector: 'pb-user-settings-dialog',
    standalone: true,

    imports: [
        FormsModule,
        DialogModule,
        ButtonModule,
        SelectModule,
        ChangePasswordDialogComponent,
    ],
    templateUrl: './user-settings-dialog.component.html',
    styleUrls: ['./user-settings-dialog.component.scss'],
})
export class UserSettingsDialogComponent {

    private readonly layoutService =
        inject(LayoutService);


    private readonly companyService = inject(CompanyService);
    private readonly marketService = inject(MarketService);



    // =============================================================
    // Inputs / Outputs
    // =============================================================

    visible = input(false);
    preferences =
        input<UserPreferences | null>(null);


    // defaultCompany = input<number | null>(null);

    // defaultMarket = input<number | null>(null);
    companyId: number | null = null;
    marketId: number | null = null;

    languageCode = '';
    timezone = '';
    locale = '';
    theme = '';
    navigationCollapsed = false;
    colorScheme = '';
    primary = '';
    surface: string | null = null;


    companies = signal<UserCompany[]>([]);
    markets = signal<Market[]>([]);

    visibleChange =
        output<boolean>();


    saveRequested =
        output<UserPreferences>();


    changePasswordRequested =
        output<string>();


    // =============================================================
    // Local state
    // =============================================================


    changePasswordDialogVisible =
        signal(false);


    // =============================================================
    // Themes
    // =============================================================

    themes = [
        {
            label: 'Aura',
            value: 'Aura',
            previewClass: 'theme-blue',
        },
        {
            label: 'Lara',
            value: 'Lara',
            previewClass: 'theme-indigo',
        },
        {
            label: 'Nora',
            value: 'Nora',
            previewClass: 'theme-teal',
        },
    ];


    selectedTheme = signal('Aura');

    primaryColors = [
        'emerald',
        'green',
        'lime',
        'orange',
        'amber',
        'yellow',
        'teal',
        'cyan',
        'sky',
        'blue',
        'indigo',
        'violet',
        'purple',
        'fuchsia',
        'pink',
        'rose',
    ];

    surfaceColors = [
        'slate',
        'gray',
        'zinc',
        'neutral',
        'stone',
        'soho',
        'viva',
        'ocean',
    ];

    constructor() {
        effect(() => {
            if (this.visible()) {
                this.loadCompanies();
                this.loadValues();
            }
        });
    }


    private loadCompanies(): void {
        this.companyService.getCompanies().subscribe({
            next: companies => {
                this.companies.set(companies);
            },
            error: error => {
                console.error(
                    'Failed to load companies',
                    error
                );
            }
        });
    }

    private loadMarkets(companyId: number): void {

        this.marketService.getMarkets(companyId).subscribe({
            next: markets => {
                this.markets.set(markets);
            },
            error: error => {
                console.error(
                    'Failed to load markets',
                    error
                );
            }
        });
    }


    // =============================================================
    // Appearance
    // =============================================================

    isDarkMode(): boolean {

        return this.layoutService.layoutConfig().darkTheme;
    }


    isLightMode(): boolean {

        return !this.layoutService
            .layoutConfig()
            .darkTheme;
    }


    setLightMode(): void {
        this.colorScheme = 'LIGHT';

        this.layoutService.layoutConfig.update(
            state => ({
                ...state,
                darkTheme: false,
            })
        );
    }


    setDarkMode(): void {
        this.colorScheme = 'DARK';

        this.layoutService.layoutConfig.update(
            state => ({
                ...state,
                darkTheme: true,
            })
        );
    }


    setTheme(theme: string): void {
        this.theme = theme;
        this.selectedTheme.set(theme);

        this.layoutService.setPreset(theme);
    }


    // =============================================================
    // Company / Market
    // =============================================================

    // onCompanyChange(): void {

    //     const markets =
    //         this.availableMarkets();

    //     const marketExists =
    //         markets.some(
    //             market =>
    //                 market.id ===
    //                 this.marketId
    //         );

    //     if (!marketExists) {
    //         this.marketId = null;
    //     }
    // }

    onCompanyChange(): void {

        this.marketId = null;

        if (this.companyId === null) {
            this.markets.set([]);
            return;
        }

        this.loadMarkets(this.companyId);
    }


    // =============================================================
    // Dialog
    // =============================================================

    onVisibleChange(
        visible: boolean
    ): void {

        if (visible) {
            this.loadCompanies();
            this.loadValues();
        }

        this.visibleChange.emit(visible);
    }

    loadValues(): void {

        const preferences = this.preferences();

        if (!preferences) {
            return;
        }

        this.companyId =
            preferences.company_id;

        this.marketId =
            preferences.market_id;

        this.languageCode =
            preferences.language_code;

        this.timezone =
            preferences.timezone;

        this.locale =
            preferences.locale;

        this.theme =
            preferences.theme;

        this.selectedTheme.set(
            preferences.theme
        );

        this.navigationCollapsed =
            preferences.navigation_collapsed;
        this.colorScheme = preferences.color_scheme;
        this.primary = preferences.primary;
        this.surface = preferences.surface;

        if (this.companyId !== null) {
            this.loadMarkets(this.companyId);
        } else {
            this.markets.set([]);
        }
    }


    close(): void {

        this.visibleChange.emit(false);
    }
    
    setPrimary(color: string): void {
        this.primary = color;
        this.layoutService.setPrimary(color);
    }


    getPrimaryColor(color: string): string {
        return `var(--p-${color}-500)`;
    }



    save(): void {

        this.saveRequested.emit({
            company_id: this.companyId,
            market_id: this.marketId,
            language_code: this.languageCode,
            timezone: this.timezone,
            locale: this.locale,
            theme: this.theme,
            color_scheme: this.colorScheme,
            primary: this.primary,
            surface: this.surface,
            navigation_collapsed:
                this.navigationCollapsed,
        });

        this.visibleChange.emit(false);
    }


    // =============================================================
    // Password
    // =============================================================

    openChangePassword(): void {

        this.changePasswordDialogVisible.set(
            true
        );
    }
}